import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  findWalletUserByWalletId, 
  connectWalletToUser, 
  disconnectWalletFromUser,
  findWalletUserByEmail,
  updateWalletUserConnection
} from '../../services/walletService';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-hot-toast';

const WalletConnect = () => {
  const { currentUser, userData, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [walletId, setWalletId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [foundWalletByEmail, setFoundWalletByEmail] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connectingByEmail, setConnectingByEmail] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [walletData, setWalletData] = useState(null);

  useEffect(() => {
    if (currentUser && userData) {
      checkWalletByEmail();
      
      // Ha van wallet adat a userData-ban, akkor beállítjuk a walletData-t
      if (userData.walletId) {
        fetchWalletData();
      }
    }
  }, [currentUser, userData]);

  const checkWalletByEmail = async () => {
    if (!currentUser || !currentUser.email) {
      console.log('No current user or email available for wallet check');
      return;
    }
    
    try {
      setEmailCheckLoading(true);
      console.log('🔍 Checking wallet by email:', currentUser.email);
      
      // Használjuk a findWalletUserByEmail függvényt a wallet felhasználó kereséséhez
      const walletUser = await findWalletUserByEmail(currentUser.email);
      
      if (walletUser && !walletUser.elonyKlubConnected) {
        console.log('✅ Found wallet user with email:', walletUser);
        setFoundWalletByEmail(walletUser);
      } else if (walletUser && walletUser.elonyKlubConnected) {
        console.log('⚠️ Found wallet user but it is already connected:', walletUser);
        setFoundWalletByEmail(null);
      } else {
        console.log('❌ No wallet user found with email:', currentUser.email);
        setFoundWalletByEmail(null);
      }
    } catch (error) {
      console.error('❌ Error checking wallet by email:', error);
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const connectWalletByEmail = async () => {
    if (!foundWalletByEmail || !currentUser) {
      console.log('No wallet or user found to connect');
      return;
    }
    
    try {
      setConnectingByEmail(true);
      console.log('🔄 Connecting wallet by email:', foundWalletByEmail);
      
      // Használjuk az updateWalletUserConnection függvényt a kapcsolat létrehozásához
      await updateWalletUserConnection(
        foundWalletByEmail.id, 
        currentUser.uid, 
        true
      );
      
      console.log('✅ Wallet connected successfully by email');
      setFoundWalletByEmail(null);
      toast.success('Wallet sikeresen összekapcsolva!');
      
      // Frissítsük a felhasználó adatait
      await fetchUserData();
    } catch (error) {
      console.error('❌ Error connecting wallet by email:', error);
      toast.error('Hiba történt a wallet összekapcsolásakor: ' + error.message);
    } finally {
      setConnectingByEmail(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    
    if (!walletId.trim()) {
      return setError('Kérjük, adja meg a wallet azonosítót');
    }
    
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      // Ellenőrizzük, hogy létezik-e a wallet
      const walletUser = await findWalletUserByWalletId(walletId);
      
      if (!walletUser) {
        return setError('A megadott wallet azonosító nem található');
      }
      
      // Ellenőrizzük, hogy a wallet nincs-e már összekapcsolva más fiókkal
      if (walletUser.elonyKlubConnected) {
        return setError('Ez a wallet már össze van kapcsolva egy másik fiókkal');
      }
      
      // Összekapcsoljuk a wallet-et a felhasználóval
      await connectWalletToUser(currentUser.id, walletId);
      
      setSuccess('A wallet sikeresen összekapcsolva a fiókjával');
      setIsConnected(true);
      setWalletInfo(walletUser);
      
      // Frissítjük az oldalt, hogy a változások megjelenjenek
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Error connecting wallet:', error);
      setError('Hiba történt a wallet összekapcsolása során: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const disconnectWallet = async () => {
    if (!walletData || !currentUser) {
      console.log('No wallet data or user found to disconnect');
      return;
    }
    
    try {
      setDisconnecting(true);
      console.log('🔄 Disconnecting wallet:', walletData);
      
      // Használjuk az updateWalletUserConnection függvényt a kapcsolat megszüntetéséhez
      await updateWalletUserConnection(
        walletData.id, 
        null, 
        false
      );
      
      console.log('✅ Wallet disconnected successfully');
      setWalletData(null);
      toast.success('Wallet sikeresen leválasztva!');
      
      // Frissítsük a felhasználó adatait
      await fetchUserData();
    } catch (error) {
      console.error('❌ Error disconnecting wallet:', error);
      toast.error('Hiba történt a wallet leválasztásakor: ' + error.message);
    } finally {
      setDisconnecting(false);
    }
  };

  // Wallet kapcsolat ellenőrzése
  const checkWalletConnection = async () => {
    try {
      if (!currentUser) {
        console.log('❌ Cannot check wallet connection: No current user');
        return;
      }
      
      console.log('🔍 Checking wallet connection for user:', currentUser.uid);
      
      // Lekérjük a felhasználó adatait a Firestore-ból
      const userQuery = query(
        collection(db, 'users'),
        where('uid', '==', currentUser.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      
      console.log(`🔍 User query results: ${userSnapshot.size} documents found`);
      
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        console.log('🔍 User data:', userData);
        
        if (userData.walletConnected && userData.walletId) {
          console.log('✅ User has connected wallet:', userData.walletId);
          setIsConnected(true);
          setWalletId(userData.walletId);
          await fetchWalletInfo();
        } else {
          console.log('❌ User has no connected wallet');
          setIsConnected(false);
          setWalletInfo(null);
        }
      } else {
        console.log('❌ No user document found for UID:', currentUser.uid);
      }
    } catch (error) {
      console.error('❌ Error checking wallet connection:', error);
    }
  };

  // Wallet információk lekérése
  const fetchWalletInfo = async () => {
    try {
      setLoading(true);
      
      if (!currentUser || !currentUser.walletId) {
        console.log('❌ Cannot fetch wallet info: No wallet ID available');
        return;
      }
      
      console.log('🔍 Fetching wallet info for wallet ID:', currentUser.walletId);
      
      // Lekérjük a wallet adatait közvetlenül a Firestore-ból
      const walletQuery = query(
        collection(db, 'walletUsers'),
        where('walletId', '==', currentUser.walletId)
      );
      const walletSnapshot = await getDocs(walletQuery);
      
      console.log(`🔍 Wallet query results: ${walletSnapshot.size} documents found`);
      
      if (!walletSnapshot.empty) {
        const walletUser = {
          id: walletSnapshot.docs[0].id,
          ...walletSnapshot.docs[0].data()
        };
        console.log('✅ Found wallet user:', walletUser);
        setWalletInfo(walletUser);
      } else {
        console.log('❌ No wallet user found with ID:', currentUser.walletId);
        setWalletInfo(null);
      }
    } catch (error) {
      console.error('❌ Error fetching wallet info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Felhasználói adatok frissítése
  const fetchUserData = async () => {
    try {
      await refreshUserData();
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
    }
  };

  // Wallet adatok lekérése
  const fetchWalletData = async () => {
    if (!userData || !userData.walletId) return;
    
    try {
      setLoading(true);
      console.log('🔍 Fetching wallet data for ID:', userData.walletId);
      
      const walletRef = doc(db, 'walletUsers', userData.walletId);
      const walletDoc = await getDoc(walletRef);
      
      if (walletDoc.exists()) {
        const wallet = {
          id: walletDoc.id,
          ...walletDoc.data()
        };
        console.log('✅ Wallet data fetched:', wallet);
        setWalletData(wallet);
      } else {
        console.log('❌ No wallet found with ID:', userData.walletId);
        setWalletData(null);
      }
    } catch (error) {
      console.error('❌ Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-4">Wallet összekapcsolás</h2>
      
      {loading && (
        <div className="flex justify-center my-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
        </div>
      )}
      
      {!loading && (
        <>
          {/* Ha van kapcsolt wallet */}
          {userData && userData.walletId && walletData ? (
            <div>
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                <p className="font-medium">Wallet sikeresen összekapcsolva!</p>
              </div>
              
              <div className="mb-4">
                <p className="text-gray-700"><span className="font-medium">Email:</span> {walletData.email}</p>
                {walletData.walletAddress && (
                  <p className="text-gray-700">
                    <span className="font-medium">Wallet cím:</span> 
                    <span className="font-mono text-sm ml-2">
                      {walletData.walletAddress.substring(0, 6)}...
                      {walletData.walletAddress.substring(walletData.walletAddress.length - 4)}
                    </span>
                  </p>
                )}
              </div>
              
              <button
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                onClick={disconnectWallet} 
                disabled={loading || disconnecting}
              >
                {loading || disconnecting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Leválasztás...
                  </>
                ) : (
                  'Wallet leválasztása'
                )}
              </button>
            </div>
          ) : (
            <>
              {/* Ha találtunk wallet-et email alapján */}
              {foundWalletByEmail ? (
                <div>
                  <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                    <p className="font-medium">Wallet fiók található az email címedhez!</p>
                    <p className="text-sm">Összekapcsolhatod a wallet fiókodat az Előny Klub fiókoddal.</p>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-gray-700"><span className="font-medium">Email:</span> {foundWalletByEmail.email}</p>
                    {foundWalletByEmail.walletAddress && (
                      <p className="text-gray-700">
                        <span className="font-medium">Wallet cím:</span> 
                        <span className="font-mono text-sm ml-2">
                          {foundWalletByEmail.walletAddress.substring(0, 6)}...
                          {foundWalletByEmail.walletAddress.substring(foundWalletByEmail.walletAddress.length - 4)}
                        </span>
                      </p>
                    )}
                  </div>
                  
                  <button 
                    className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50"
                    onClick={connectWalletByEmail} 
                    disabled={loading || emailCheckLoading || connectingByEmail}
                  >
                    {loading || connectingByEmail ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Összekapcsolás...
                      </>
                    ) : (
                      'Összekapcsolás'
                    )}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
                    <p className="font-medium">Nincs még összekapcsolt wallet fiókod</p>
                    <p className="text-sm">Regisztrálj a wallet alkalmazásban, majd kapcsold össze a fiókodat.</p>
                  </div>
                  
                  <p className="text-gray-700 mb-4">
                    A wallet alkalmazásban regisztrálj ugyanazzal az email címmel, amit az Előny Klub fiókodhoz használsz, 
                    majd térj vissza ide az összekapcsoláshoz.
                  </p>
                  
                  <button 
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    onClick={checkWalletByEmail} 
                    disabled={loading || emailCheckLoading}
                  >
                    {emailCheckLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Ellenőrzés...
                      </>
                    ) : (
                      'Wallet ellenőrzése'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WalletConnect; 